import { vehicleLabelOf } from '@voll-intelligence/intelligence-ui-kit';
import type {
  ChatUi,
  ChatUiRentalOffer,
  RentalOfferPageHint,
} from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { fmtMoneyWhole } from '../../shared/ui/flight_format.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { RentalOffersList } from './ui/rental_offers.js';

export { RentalOffersList } from './ui/rental_offers.js';

type RentalOffersUi = Extract<ChatUi, { kind: 'rental_offers' }>;

/**
 * O toque no card (e o "Alugar" da tela em diálogo) vira um `rental-select` com
 * o `offerId` determinístico — o agente resolve `start_checkout` por id, sem o
 * LLM reinterpretar a fala. O eco é a escolha em prosa ("Quero o Grupo B ·
 * Hyundai HB20S ou similar na Localiza, R$ 378."), no idioma da conversa.
 *
 * **Componente e não um lambda no `render` (#2327, mesmo padrão de
 * `bus/bus_offers/index.tsx`).** Traduzir exige o locale, o locale mora no
 * `GenUiConfig`, e `render` é uma função de catálogo: hook ali não roda.
 */
function RentalOffersEntry({
  ui,
  note,
  readOnly,
  replay,
  dispatch,
}: Readonly<{
  ui: RentalOffersUi;
  note?: string;
  readOnly?: boolean;
  replay?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t } = useGenUiI18n();
  const select = (offer: ChatUiRentalOffer, _position: number, page?: RentalOfferPageHint) => {
    const vehicle = vehicleLabelOf(offer, t('rental.vehicle.orSimilar'));
    dispatch({
      type: 'rental-select',
      offerId: offer.offerId,
      // Preço TOTAL que o cliente está vendo no card — baseline da comparação
      // de preço no agente, threadado fora do texto livre.
      price: offer.price,
      // A página de "Ver todos" que trouxe a oferta (#5833): o agente só tem a
      // busca do card, e relê esta página para achar a oferta.
      ...(page ? { page } : {}),
      text: t('rentalOffers.pickText', {
        title: [offer.category, vehicle].filter(Boolean).join(' · '),
        company: offer.rentalCompany,
        total: fmtMoneyWhole(offer.price),
      }),
    });
  };
  return (
    <RentalOffersList
      offers={ui.offers}
      metadata={ui.metadata}
      searchContext={ui.searchContext}
      policies={ui.policies}
      variant={ui.variant}
      selectedOfferId={ui.selectedOfferId}
      readOnly={readOnly}
      replay={replay}
      note={note}
      onSelect={select}
    />
  );
}

/** Entrada de catálogo do `rental_offers` (slot chat). O recibo (`collapsed`) e
 *  o card congelado pelo shell não escolhem mais nada. */
export const rentalOffersEntry: GenUiEntry<RentalOffersUi> = {
  kind: 'rental_offers',
  slot: 'chat',
  policy: {
    productOf: () => 'rental',
    // Variante colapsada (pós-seleção) congela para sempre nessa linha, mesmo
    // que a mensagem seja o turno ativo do agente.
    frozenWhen: (ui) => ui.variant === 'collapsed',
  },
  render: ({ ui, note, readOnly, replay, dispatch }) => (
    <RentalOffersEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      {...(replay ? { replay } : {})}
      dispatch={dispatch}
    />
  ),
};
